import { useMemo } from 'react'
import PropTypes from 'prop-types'
import ReactECharts from 'echarts-for-react'

/**
 * EducationRatiosChart (Bare) - Chart only, no wrapper
 * For embedding in narrative HTML templates
 */
function EducationRatiosChart({ data }) {
  const ratioData = useMemo(() => {
    if (!data || typeof data !== 'object') return []

    const ratios = [
      {
        key: 'pupilTeacherRatio',
        name: 'Pupil-Teacher Ratio',
        value: data.pupilTeacherRatio,
        standard: 40,
        unit: ':1',
        color: '#3B82F6',
      },
      {
        key: 'pupilClassroomRatio',
        name: 'Pupil-Classroom Ratio',
        value: data.pupilClassroomRatio,
        standard: 53,
        unit: ':1',
        color: '#10B981',
      },
      {
        key: 'pupilStanceRatio',
        name: 'Pupil-Stance Ratio',
        value: data.pupilStanceRatio,
        standard: 3,
        unit: ':1',
        color: '#F59E0B',
      },
      {
        key: 'pupilTextbookRatio',
        name: 'Pupil-Textbook Ratio',
        value: data.pupilTextbookRatio,
        standard: 3,
        unit: ':1',
        color: '#8B5CF6',
      },
    ]

    return ratios.filter(ratio => ratio.value !== undefined && ratio.value > 0)
  }, [data])

  const comparisonChartOptions = useMemo(
    () => ({
      tooltip: {
        trigger: 'axis',
        axisPointer: {
          type: 'shadow',
        },
        formatter: params => {
          const actual = params[0]
          const standard = params[1]
          const ratio = ratioData.find(r => r.name === actual.name)

          let status = ''
          if (
            ratio.key === 'pupilTeacherRatio' ||
            ratio.key === 'pupilClassroomRatio'
          ) {
            status =
              actual.value <= standard.value
                ? '✓ Meets Standard'
                : '⚠️ Exceeds Standard'
          } else {
            status =
              actual.value <= standard.value
                ? '✓ Within Standard'
                : '⚠️ Above Standard'
          }

          return `<strong>${actual.name}</strong><br/>
                  Actual: ${actual.value}${ratio.unit}<br/>
                  Standard: ${standard.value}${ratio.unit}<br/>
                  <span style="color: ${actual.value <= standard.value ? '#10B981' : '#F59E0B'}">${status}</span>`
        },
      },
      legend: {
        data: ['Actual', 'Standard'],
        bottom: 0,
      },
      grid: {
        left: '3%',
        right: '4%',
        bottom: '15%',
        top: '3%',
        containLabel: true,
      },
      xAxis: {
        type: 'category',
        data: ratioData.map(r => r.name),
        axisLabel: {
          interval: 0,
          rotate: 30,
          fontSize: 11,
        },
      },
      yAxis: {
        type: 'value',
        name: 'Ratio',
      },
      series: [
        {
          name: 'Actual',
          type: 'bar',
          data: ratioData.map(r => ({
            value: r.value,
            itemStyle: {
              color: r.color,
            },
          })),
          label: {
            show: true,
            position: 'top',
            formatter: params => {
              const ratio = ratioData[params.dataIndex]
              return `${params.value}${ratio.unit}`
            },
          },
        },
        {
          name: 'Standard',
          type: 'bar',
          data: ratioData.map(r => r.standard),
          itemStyle: {
            color: '#94A3B8',
          },
          label: {
            show: true,
            position: 'top',
            formatter: params => {
              const ratio = ratioData[params.dataIndex]
              return `${params.value}${ratio.unit}`
            },
          },
        },
      ],
    }),
    [ratioData]
  )

  if (!data || ratioData.length === 0) {
    return (
      <div className="text-center py-8 text-gray-500">
        No education ratio data available
      </div>
    )
  }

  return (
    <ReactECharts option={comparisonChartOptions} style={{ height: '350px' }} />
  )
}

EducationRatiosChart.propTypes = {
  data: PropTypes.shape({
    pupilTeacherRatio: PropTypes.number,
    pupilClassroomRatio: PropTypes.number,
    pupilStanceRatio: PropTypes.number,
    pupilTextbookRatio: PropTypes.number,
  }),
}

export default EducationRatiosChart
